import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const NotFound = () => {
  const [searchQuery, setSearchQuery] = useState("");
  
  // Define key navigation categories - update these with your actual site structure
  const navigationLinks = [
    { name: "About", links: [
      { to: "/about", label: "About Us" },
      { to: "/News", label: "News" },
      { to: "/contact-us", label: "Contact Us" },
      { to: "/pricing", label: "Pricing" }

    ]},
    { name: "Services", links: [
      { to: "/spectral-library/analyse", label: "Analyse Spectra" },
      { to: "/spectral-library/database", label: "Spectral Database" },
      { to: "/spectral-library/upload", label: "Upload Spectra" }
    ]},
  ];

  // Plastic Analytics color palette
  const colors = {
    primary: "#111d4a",      // Dark blue - from your chat widget
    secondary: "#1e2d64",    // Slightly lighter blue for hover states
    accent: "#5a7184",       // Medium slate blue for accents
    light: "#f8fafc",        // Very light background
    white: "#ffffff",        // White
    border: "rgba(17, 29, 74, 0.15)", // Transparent primary for borders
    success: "#16a34a",      // Green for success messages
    error: "#ef4444"         // Red for error states
  };

  // Inline styles using JS objects
  const styles = {
    container: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: colors.light,
      padding: '20px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
    content: {
      maxWidth: '800px',
      width: '100%',
      backgroundColor: colors.white,
      padding: '40px',
      borderRadius: '12px',
      boxShadow: '0 10px 30px rgba(17, 29, 74, 0.08)',
      border: `1px solid ${colors.border}`,
    },
    logoContainer: {
      textAlign: 'center',
      marginBottom: '30px',
    },
    logo: {
      height: '50px',
      marginBottom: '20px',
    },
    headerSection: {
      textAlign: 'center',
      marginBottom: '40px',
      position: 'relative',
    },
    decorativeLine: {
      position: 'absolute',
      top: '0',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '60px',
      height: '4px',
      backgroundColor: colors.primary,
      borderRadius: '2px',
    },
    heading: {
      fontSize: '72px',
      color: colors.primary,
      marginBottom: '10px',
      fontWeight: '700',
      lineHeight: '1.1',
    },
    subheading: {
      fontSize: '24px',
      color: colors.primary,
      fontWeight: '600',
      marginBottom: '20px',
    },
    description: {
      fontSize: '16px',
      color: colors.accent,
      marginBottom: '30px',
      maxWidth: '600px',
      margin: '0 auto 30px',
      lineHeight: '1.6',
    },
    searchContainer: {
      marginBottom: '30px',
    },
    searchInput: {
      width: '100%',
      padding: '15px 20px',
      fontSize: '16px',
      border: `1px solid ${colors.border}`,
      borderRadius: '6px',
      outline: 'none',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box',
    },
    searchInputFocus: {
      borderColor: colors.primary,
      boxShadow: `0 0 0 3px ${colors.border}`,
    },
    buttonsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '15px',
      alignItems: 'center',
      marginBottom: '40px',
    },
    homeLink: {
      display: 'inline-block',
      backgroundColor: colors.primary,
      color: colors.white,
      padding: '12px 24px',
      borderRadius: '6px',
      textDecoration: 'none',
      fontSize: '16px',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      border: `2px solid ${colors.primary}`,
    },
    homeLinkHover: {
      backgroundColor: colors.secondary,
    },
    backButton: {
      display: 'inline-block',
      backgroundColor: 'transparent',
      color: colors.primary,
      padding: '12px 24px',
      borderRadius: '6px',
      textDecoration: 'none',
      fontSize: '16px',
      fontWeight: '500',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    backButtonHover: {
      backgroundColor: 'rgba(17, 29, 74, 0.05)',
    },
    navigationSection: {
      backgroundColor: 'rgba(17, 29, 74, 0.03)',
      borderRadius: '10px',
      padding: '30px',
      marginTop: '20px',
    },
    navigationTitle: {
      fontSize: '18px',
      color: colors.primary,
      fontWeight: '600',
      marginBottom: '25px',
      textAlign: 'center',
    },
    categoriesContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-around',
      gap: '20px',
    },
    category: {
      minWidth: '180px',
    },
    categoryTitle: {
      fontSize: '16px',
      color: colors.primary,
      fontWeight: '700',
      marginBottom: '15px',
      paddingBottom: '8px',
      borderBottom: `2px solid ${colors.border}`,
    },
    categoryLinks: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    link: {
      display: 'flex',
      alignItems: 'center',
      color: colors.primary,
      textDecoration: 'none',
      fontSize: '15px',
      transition: 'all 0.2s ease',
      padding: '5px 0',
    },
    linkHover: {
      color: colors.secondary,
    },
    linkIcon: {
      marginRight: '8px',
      fontSize: '14px',
    },
    footer: {
      marginTop: '40px',
      textAlign: 'center',
      color: colors.accent,
      fontSize: '14px',
    },
    footerLink: {
      color: colors.primary,
      textDecoration: 'none',
      fontWeight: '500',
    },
    footerLinkHover: {
      color: colors.secondary,
    },
  };

  const handleBackClick = () => {
    window.history.back();
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        when: "beforeChildren",
        staggerChildren: 0.1,
        duration: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const searchVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.98 },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: { 
        delay: 0.2, 
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1] 
      }
    }
  };

  return (
    <div style={styles.container}>
      <motion.div 
        style={styles.content}
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.div style={styles.logoContainer} variants={itemVariants}>
          {/* Replace with your actual logo */}
          <img 
            src="/your-plastic-analytics-logo.png" 
            alt="Plastic Analytics Logo" 
            style={styles.logo}
          />
        </motion.div>

        <motion.div style={styles.headerSection} variants={itemVariants}>
          <div style={styles.decorativeLine}></div>
          <h1 style={styles.heading}>404</h1>
          <h2 style={styles.subheading}>Page Not Found</h2>
          <p style={styles.description}>
            We're sorry, but the page you're looking for doesn't exist or has been moved.
            Our data analysis couldn't identify this resource in our database.
          </p>
        </motion.div>

        <motion.div style={styles.searchContainer} variants={searchVariants}>
          <input
            type="text"
            placeholder="Search for plastic analysis resources..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={styles.searchInput}
            onFocus={(e) => Object.assign(e.target.style, styles.searchInputFocus)}
            onBlur={(e) => {
              e.target.style.borderColor = colors.border;
              e.target.style.boxShadow = 'none';
            }}
          />
        </motion.div>

        <motion.div style={styles.buttonsContainer} variants={itemVariants}>
          <Link
            to="/"
            style={styles.homeLink}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = styles.homeLinkHover.backgroundColor;
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = styles.homeLink.backgroundColor;
            }}
          >
            Return to Homepage
          </Link>
          
          <button 
            style={styles.backButton}
            onClick={handleBackClick}
            onMouseOver={(e) => e.target.style.backgroundColor = styles.backButtonHover.backgroundColor}
            onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            ← Go Back
          </button>
        </motion.div>

        <motion.div 
          style={styles.navigationSection}
          variants={itemVariants}
        >
          <h3 style={styles.navigationTitle}>Explore Our Platform</h3>
          <div style={styles.categoriesContainer}>
            {navigationLinks.map((category, index) => (
              <div key={index} style={styles.category}>
                <h4 style={styles.categoryTitle}>{category.name}</h4>
                <div style={styles.categoryLinks}>
                  {category.links.map((link, linkIndex) => (
                    <Link
                      key={linkIndex}
                      to={link.to}
                      style={styles.link}
                      onMouseOver={(e) => e.target.style.color = styles.linkHover.color}
                      onMouseOut={(e) => e.target.style.color = styles.link.color}
                    >
                      <span style={styles.linkIcon}>›</span>
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div style={styles.footer} variants={itemVariants}>
          <p>
            Need assistance with plastic data analysis? <a 
              href="/contact-us"
              style={styles.footerLink}
              onMouseOver={(e) => e.target.style.color = styles.footerLinkHover.color}
              onMouseOut={(e) => e.target.style.color = styles.footerLink.color}
            >
              Contact our support team
            </a>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default NotFound;